import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CONTACT_EMAIL = "Derek@ViralPulseAgency.com";

const LegalLayout = ({ title, updated, children, testid }) => (
  <div data-testid={testid} className="min-h-screen bg-[#05050A] text-white">
    <header className="border-b border-white/10">
      <div className="mx-auto flex h-[76px] max-w-4xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="legal-logo-link" className="flex items-center gap-3">
          <img src="/assets/vp-mark.png" alt="Viral Pulse Agency" className="h-9 w-auto" />
          <span className="font-display text-base font-extrabold tracking-tight">VIRAL PULSE</span>
        </Link>
        <Link
          to="/"
          data-testid="legal-back-link"
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:text-purple-400"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back To Site
        </Link>
      </div>
    </header>
    <main className="mx-auto max-w-4xl px-5 py-14 sm:px-8 sm:py-16">
      <h1 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">{title}</h1>
      <p className="mt-3 text-sm text-zinc-500">Last updated: {updated}</p>
      <div className="mt-10 space-y-9">{children}</div>
    </main>
    <footer className="border-t border-white/10">
      <p className="mx-auto max-w-4xl px-5 py-7 text-xs text-zinc-600 sm:px-8">
        © 2026 Viral Pulse Agency. All rights reserved. Las Vegas, NV.
      </p>
    </footer>
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display text-xl font-extrabold tracking-tight text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-zinc-400">{children}</div>
  </section>
);

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="July 2026" testid="privacy-page">
      <Section title="What We Collect">
        <p>
          When you request a free marketing audit or contact us through this
          website, we collect the information you give us: your name, email
          address, phone number, business name, website, and the details you
          share about your business and marketing.
        </p>
        <p>
          We also collect basic, anonymous usage data (such as pages visited) to
          understand how the website is used.
        </p>
      </Section>
      <Section title="How We Use It">
        <p>We use your information to:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Review your business and prepare your free marketing audit</li>
          <li>Contact you about your audit and our services</li>
          <li>Improve this website and our marketing</li>
        </ul>
        <p>We do not sell your personal information. Ever.</p>
      </Section>
      <Section title="Who We Share It With">
        <p>
          Your information stays inside Viral Pulse. We may use trusted service
          providers (for example, email delivery and website hosting) to operate
          this site, and they only access what's needed to do their job.
        </p>
      </Section>
      <Section title="Your Choices">
        <p>
          Want a copy of your information, or want it deleted? Email us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-400 hover:text-purple-300">
            {CONTACT_EMAIL}
          </a>{" "}
          and we'll take care of it.
        </p>
      </Section>
      <Section title="Contact">
        <p>
          Questions about this policy? Reach us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-400 hover:text-purple-300">
            {CONTACT_EMAIL}
          </a>
          . Viral Pulse Agency, Las Vegas, NV.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updated="July 2026" testid="terms-page">
      <Section title="The Basics">
        <p>
          This website is operated by Viral Pulse Agency, a digital marketing
          agency based in Las Vegas, Nevada. By using this site, you agree to
          these terms.
        </p>
      </Section>
      <Section title="Free Marketing Audits">
        <p>
          Requesting a free audit through this site simply asks us to review
          your business and share what we'd do differently. It creates no
          obligation on either side, and it is not a contract for services.
        </p>
      </Section>
      <Section title="Results and Examples">
        <p>
          Marketing results vary. Any campaign figures, case studies or
          calculations shown on this site are illustrative examples of what
          lead-generation campaigns can look like — not promises or guarantees
          of what your business will achieve. Actual results depend on your
          industry, market, offer, budget, and other factors.
        </p>
      </Section>
      <Section title="Intellectual Property">
        <p>
          The content, design and branding on this site belong to Viral Pulse
          Agency. Please don't copy or reuse them without permission.
        </p>
      </Section>
      <Section title="Limitation of Liability">
        <p>
          This website is provided as-is. To the fullest extent permitted by
          law, Viral Pulse Agency is not liable for any indirect or
          consequential damages arising from use of this site.
        </p>
      </Section>
      <Section title="Governing Law">
        <p>These terms are governed by the laws of the State of Nevada.</p>
      </Section>
      <Section title="Contact">
        <p>
          Questions about these terms? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-400 hover:text-purple-300">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}
